import { useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"
import { CommandItem } from "@productlane/ui/src/components/internal/command"
import { queries } from "@productlane/zero/queries"

import type { SharedFilterItemsProps } from "./types"
import { useRoadmapFilterState } from "../roadmap-filter-state"

/** Linear's own label colour, as the dot Linear shows next to a label. */
export function LabelDot({ color }: { color: string }) {
	return (
		<span
			aria-hidden
			className="size-2 flex-none rounded-full"
			style={{ backgroundColor: color }}
		/>
	)
}

export interface LabelOption {
	key: string
	name: string
	color: string
	ids: string[]
}

/**
 * Each Linear team can have its own label with the same name, so labels are
 * offered once per name and filter by every id behind it.
 */
export function labelOptions(
	labels: ReadonlyArray<{ id: string; name: string; color: string }>,
): LabelOption[] {
	const byName = new Map<string, LabelOption>()
	for (const label of labels) {
		const key = label.name.trim().toLowerCase()
		const option = byName.get(key)
		if (option) option.ids.push(label.id)
		else
			byName.set(key, {
				key,
				name: label.name,
				color: label.color,
				ids: [label.id],
			})
	}
	return [...byName.values()]
}

export function useLabelOptions(): LabelOption[] {
	const [labels] = useQuery(queries.linearLabels())
	return useMemo(() => labelOptions(labels), [labels])
}

export function LabelFilterItems({ onSelect }: SharedFilterItemsProps) {
	const options = useLabelOptions()
	const { labelIds, toggleLabels } = useRoadmapFilterState()

	return (
		<>
			{options.map((option) => {
				const isSelected = option.ids.some((id) => labelIds.includes(id))
				return (
					<CommandItem
						key={option.key}
						value={option.key}
						onSelect={() => {
							toggleLabels(option.ids)
							onSelect()
						}}
					>
						<div className="flex w-full items-center gap-2">
							<LabelDot color={option.color} />
							<span className="flex-1 truncate">{option.name}</span>
							<Check
								className={cn("h-4 w-4 shrink-0", {
									"opacity-100": isSelected,
									"opacity-0": !isSelected,
								})}
							/>
						</div>
					</CommandItem>
				)
			})}
		</>
	)
}
